import { http } from './request'
import { apiConfig } from './config'
import {
  asJobStart,
  asJobStatus,
  downloadExportJobExcel,
  type OverseasExportJobStart,
  type OverseasExportJobStatus,
} from './overseasSensitiveExport'
import {
  OVERSEAS_IMPORT_HTTP_TIMEOUT_MS,
  OVERSEAS_IMPORT_PREVIEW_HTTP_TIMEOUT_MS,
  OVERSEAS_IMPORT_PREPARE_HTTP_TIMEOUT_MS,
  parseImportChunkResult,
  parseImportPreviewResult,
} from '@/types/importPreview'
import { toPlainOrCipherPlaceholder } from '@/utils/confidentialityDisplay'

export type CompanyStatus = 'pending' | 'normal' | 'rejected'
export type UnitType = 'enterprise' | 'project'
/** 企业属性：字典 enterprise_attributes 的存库值（或历史中文/兼容值） */
export type CompanyProperty = string

/** 列表接口审核状态（与后端一致） */
export type CompanyApiAuditStatus = 'PENDING' | 'APPROVED' | 'REJECTED'

export interface EnterpriseAuditHistoryItem {
  id: string
  enterpriseId: string
  action: 'SUBMIT' | 'CLAIM' | 'RESUBMIT' | 'AUDIT' | string
  fromStatus?: CompanyApiAuditStatus | string
  toStatus: CompanyApiAuditStatus | string
  operatorUserId?: string
  operatorUserName?: string
  operateTime?: string
  remark?: string
  snapshotJson?: string
  createTime?: string
}

const SUBMIT_HISTORY_ACTIONS = new Set(['SUBMIT', 'CLAIM', 'RESUBMIT'])

export function enterpriseAuditActionLabel(action: unknown): string {
  const s = String(action ?? '')
    .trim()
    .toUpperCase()
  if (s === 'SUBMIT') return '初始化提交'
  if (s === 'CLAIM') return '企业账号认领'
  if (s === 'RESUBMIT') return '重新提交审核'
  if (s === 'AUDIT') return '审核'
  return String(action ?? '').trim()
}

export function enterpriseAuditStatusLabel(v: unknown): string {
  const s = String(v ?? '')
    .trim()
    .toUpperCase()
  if (!s) return '—'
  if (s === 'PENDING') return '待审核'
  if (s === 'APPROVED') return '已通过'
  if (s === 'REJECTED') return '已拒绝'
  return s
}

/** 最近一次送审（初始化 / 认领 / 重新提交），用于审核页展示本次进审原因 */
export function latestEnterpriseSubmitHistory(
  list: EnterpriseAuditHistoryItem[],
): EnterpriseAuditHistoryItem | undefined {
  return list.find((h) =>
    SUBMIT_HISTORY_ACTIONS.has(
      String(h.action ?? '')
        .trim()
        .toUpperCase(),
    ),
  )
}

/** 最近一次审核结论（通过/驳回），与送审记录分开 */
export function latestEnterpriseAuditDecision(
  list: EnterpriseAuditHistoryItem[],
): EnterpriseAuditHistoryItem | undefined {
  return list.find(
    (h) =>
      String(h.action ?? '')
        .trim()
        .toUpperCase() === 'AUDIT',
  )
}

export interface CompanyItem {
  id: string
  unitType: UnitType
  unitName: string
  socialCreditCode: string
  industryCategory: string
  companyProperty: CompanyProperty
  /** 国内地区路径（省-市-区），用于列表「省内地区」列；与国外地址拆分 */
  domesticAddressPath?: string
  /** domestic_address 中换行后的国内详细地址（门牌等） */
  domesticStreetAddress?: string
  /** 国内末级区划 ID / 区划码，编辑回显级联 */
  domesticGeonameid?: number
  /** 国内区县区划码（与库 domestic_address_code 一致） */
  domesticAddressCode?: string
  /** 国外地区 GeoNames ID（与库 overseas_geonameid 一致） */
  overseasGeonameid?: number
  /** 国家二位码 */
  overseasCountry?: string
  /** 国家中文名（仅国家，不含省州） */
  overseasCountryName?: string
  /** 境外末级地区名称（与库 overseas_address 一致） */
  overseasAddress: string
  /** 国外详细地址（与库 overseas_detail_address 一致） */
  overseasDetailAddress?: string
  chineseEmployeeCount?: number | null
  /** 投资领域，仅企业，字典 investment_field */
  investmentField?: string
  /** 企业设立方式，仅企业，字典 establish_method */
  establishMethod?: string
  /** 中方投资规模，仅企业，原文保存 */
  investmentScale?: string
  /** 中方投资占比，仅企业，原文保存 */
  investmentRatio?: string
  /** 运营状况，仅企业，字典 operation_status */
  operationStatus?: string
  /** 所属大类，仅项目，字典 project_category */
  projectCategory?: string
  /** 项目所属企业名称，仅项目 */
  ownerEnterpriseName?: string
  /** 项目简介，仅项目 */
  projectSummary?: string
  /** 累计合同额，仅项目，原文保存 */
  contractAmountUsd?: string
  /** 累计营业额，仅项目，原文保存 */
  turnoverAmountUsd?: string
  /** 签约时间，仅项目，YYYY-MM-DD */
  contractSignDate?: string
  /** 境内企业主要负责人，仅项目 */
  domesticPrincipal?: string
  /** 所在洲，由所在国家带出；只读，保存请求不传 */
  continent?: string
  domesticDispatchAspect: string
  principal: string
  principalPhone: string
  foreignGeoCoordinate: string
  status: CompanyStatus
  rejectReason?: string
  legalPerson?: string
  legalPersonPhone?: string
  legalPersonIdCard?: string
  registeredCapital?: string
  establishDate?: string
  businessScope?: string
  businessTerm?: string
  issueLicenseDate?: string
  businessLicensPhoto?: string
  /** 接口 CommonEntity / 审核字段（详情展示） */
  createTime?: string
  updateTime?: string
  createUser?: string
  createUserName?: string
  /** 创建人 userMode：100 政府 / 200 个人 / 300 企业 */
  createUserMode?: number
  /** 创建人登录账号 */
  createUserAccount?: string
  /** 关联账号（系统用户 ID） */
  linkedUserId?: string
  /** 关联账号中文姓名 */
  linkedUserName?: string
  /** 关联账号类型：100 政府 / 200 个人 / 300 企业 */
  linkedUserMode?: number
  /** 是否代办：1 待认领，0 已归属 */
  proxyForOthers?: string | number | boolean
  updateUser?: string
  updateUserName?: string
  auditUser?: string
  /** 审核人姓名（接口对 auditUser 按用户表 name 翻译，字段名与 createUserName 形式一致） */
  auditUserName?: string
  auditTime?: string
  auditRemark?: string
  /**
   * 完整性状态：VALID 完整 / TAMPERED 已篡改 / UNSIGNED 未签名 / DISABLED 未启用
   */
  integrityStatus?: 'VALID' | 'TAMPERED' | 'UNSIGNED' | 'DISABLED' | string
}

/** 列表行：含接口原始对象，仅查看详情用；编辑时需 `companyListRowToCompanyItem` 去掉 listRawRecord */
export interface CompanyListRow extends CompanyItem {
  listRawRecord: Record<string, unknown>
}

/** 供表单/审核等仅需要 CompanyItem 的场景，避免把 listRawRecord 带入编辑态 */
export function companyListRowToCompanyItem(row: CompanyListRow): CompanyItem {
  const { listRawRecord: _, ...item } = row
  return item as CompanyItem
}

/** 将分页/详情接口单条 raw 转为 CompanyItem（不含 listRawRecord），供详情弹窗等与表单字段对齐展示 */
export function enterpriseRecordToCompanyItem(raw: unknown): CompanyItem | null {
  const row = mapOverseasEnterpriseRecordToCompanyItem(raw)
  return row ? companyListRowToCompanyItem(row) : null
}

/** GET /biz/overseas/enterprise/page */
export interface CompanyPageParams {
  current: number
  size: number
  /** 企业名称 / 统一社会信用代码 */
  searchKey?: string
  /** 单位类型：不传或 all 为全部 */
  type?: UnitType | 'all'
  /**
   * 国内区划码前缀（与人员列表一致：省/市/区县级联按层级截断后右匹配 domestic_address_code）
   */
  areaCode?: string
  /** 审核状态：不传或 all 为全部 */
  status?: CompanyApiAuditStatus | 'all'
}

export interface CompanyPageResult {
  list: CompanyListRow[]
  total: number
}

export type CompanySavePayload = Pick<
  CompanyItem,
  | 'unitType'
  | 'unitName'
  | 'socialCreditCode'
  | 'industryCategory'
  | 'companyProperty'
  | 'overseasAddress'
  | 'chineseEmployeeCount'
  | 'domesticDispatchAspect'
  | 'principal'
  | 'principalPhone'
  | 'foreignGeoCoordinate'
  | 'status'
>

/** POST /biz/overseas/enterprise/add */
export interface EnterpriseAddPayload {
  /** 与库表一致：企业 / 项目 */
  bizType: '企业' | '项目'
  name: string
  creditCode: string
  industryCategory: string
  enterpriseAttribute: string
  /**
   * 国内地区 ID（原 GeoNames/兼容字段），已停用：请求体勿传，后端不落库；列后续将移除。
   */
  domesticGeonameid?: number
  /** 国内区县行政区划码（末级，必选到区县后由前端写入） */
  domesticAddressCode: string
  chineseEmployeeCount?: number | null
  /** 仅企业；编辑时空字符串清空。不要传 continent。 */
  investmentField?: string
  establishMethod?: string
  investmentScale?: string
  investmentRatio?: string
  operationStatus?: string
  /** 仅项目；编辑时空字符串清空。 */
  projectCategory?: string
  ownerEnterpriseName?: string
  projectSummary?: string
  contractAmountUsd?: string
  turnoverAmountUsd?: string
  contractSignDate?: string
  domesticPrincipal?: string
  domesticDepartUnit: string
  principalPerson: string
  contactPhone: string
  /** 境外坐标；可与纬度同时省略 */
  overseasLongitude?: number
  overseasLatitude?: number
  /** 国外行政区划末级 geonameid */
  overseasGeonameid: number
  /** 国家二位码 */
  overseasCountry?: string
  /** 国家中文名（仅国家） */
  overseasCountryName?: string
  legalPerson?: string
  legalPersonPhone?: string
  legalPersonIdCard?: string
  domesticAddress?: string
  /** 境外末级地区名称 */
  overseasAddress?: string
  /** 国外详细地址 */
  overseasDetailAddress?: string
  registeredCapital?: string
  establishDate?: string
  businessScope?: string
  businessTerm?: string
  issueLicenseDate?: string
  businessLicensPhoto?: string
}

/** POST /biz/overseas/enterprise/edit（字段与新增一致，多 id；驳回后可带 status 重新提交） */
export type EnterpriseEditPayload = EnterpriseAddPayload & {
  id: string
  /**
   * 重新提交审核状态：
   * - 已通过/已拒绝：编辑后可置为 PENDING 进入待审核
   * - 已拒绝：也允许维持 REJECTED（仅更新数据但不发起重新审核，是否生效以后台为准）
   * 不可传 APPROVED。
   */
  status?: Extract<CompanyApiAuditStatus, 'PENDING' | 'REJECTED'>
}

/** 企业属性直接存接口字典值（或历史中文），不再做硬编码映射 */
export function mapCompanyPropertyToEnterpriseAttribute(p: CompanyProperty): string {
  return String(p ?? '').trim()
}

/** 外国地理坐标字符串「经度,纬度」拆分为经纬度（支持中英文逗号） */
export function parseForeignCoordinateToLonLat(input: string): { lon: number; lat: number } | null {
  const s = input.trim()
  if (!s) return null
  const parts = s
    .split(/[,，]/)
    .map((x) => x.trim())
    .filter(Boolean)
  if (parts.length < 2) return null
  const lon = Number(parts[0])
  const lat = Number(parts[1])
  if (Number.isNaN(lon) || Number.isNaN(lat)) return null
  return { lon, lat }
}

/** 映射后的审核状态 → 与列表 a-tag 一致的文案与颜色 */
export function companyItemStatusDisplay(status: CompanyStatus): {
  text: string
  color: 'processing' | 'success' | 'error'
} {
  if (status === 'pending') return { text: '待审核', color: 'processing' }
  if (status === 'rejected') return { text: '已拒绝', color: 'error' }
  return { text: '已通过', color: 'success' }
}

/** 接口原始 status（PENDING / APPROVED / REJECTED）→ 中文，与列表一致 */
export function formatEnterpriseApiStatusLabel(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—'
  const u = String(value).toUpperCase()
  if (u === 'PENDING') return '待审核'
  if (u === 'REJECTED') return '已拒绝'
  if (u === 'APPROVED') return '已通过'
  return String(value)
}

/** 接口原始 status → 与列表 a-tag 一致的颜色 */
export function enterpriseApiStatusTagColor(
  value: unknown,
): 'default' | 'processing' | 'success' | 'error' {
  const u = String(value ?? '').toUpperCase()
  if (u === 'PENDING') return 'processing'
  if (u === 'REJECTED') return 'error'
  if (u === 'APPROVED') return 'success'
  return 'default'
}

/** 省内地区展示：地区字段为「省-市-区」时用「 / 」展示市、区 */
export function formatCompanyInnerRegion(overseasAddress: string): string {
  const raw = overseasAddress?.trim() ?? ''
  if (!raw) return '—'
  const parts = raw
    .split('-')
    .map((s) => s.trim())
    .filter(Boolean)
  if (parts.length <= 1) return raw
  return parts.slice(1).join(' / ')
}

interface OverseasEnterprisePageData {
  records?: unknown[]
  list?: unknown[]
  total?: number
  size?: number
  current?: number
}

function pickStr(o: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = o[k]
    if (v != null && v !== '') return String(v)
  }
  return ''
}

function pickId(o: Record<string, unknown>): string {
  const v = o.id
  if (v != null && v !== '') return String(v)
  return ''
}

function pickNum(o: Record<string, unknown>, ...keys: string[]): number | undefined {
  for (const k of keys) {
    const v = o[k]
    if (typeof v === 'number' && !Number.isNaN(v)) return v
    if (typeof v === 'string' && v.trim() !== '') {
      const n = Number(v)
      if (!Number.isNaN(n)) return n
    }
  }
  return undefined
}

function mapApiStatusToCompany(s: unknown): CompanyStatus {
  const u = String(s ?? '').toUpperCase()
  if (u === 'PENDING') return 'pending'
  if (u === 'REJECTED') return 'rejected'
  if (u === 'APPROVED') return 'normal'
  return 'pending'
}

function mapUnitTypeFromRecord(v: unknown): UnitType {
  const s = String(v ?? '').toUpperCase()
  if (s === 'PROJECT' || s === '项目' || /项目/.test(String(v ?? ''))) return 'project'
  if (s === 'ENTERPRISE' || s === '企业' || /企业/.test(String(v ?? ''))) return 'enterprise'
  const lower = String(v ?? '').toLowerCase()
  if (lower === 'project') return 'project'
  if (lower === 'enterprise') return 'enterprise'
  return 'enterprise'
}

function mapCompanyPropertyFromApi(v: unknown): CompanyProperty {
  return String(v ?? '').trim()
}

/** domestic_address：首行省-市-区，换行后为详细地址 */
function splitDomesticStoredAddress(raw: string): { regionLine: string; street: string } {
  const s = raw.trim()
  if (!s) return { regionLine: '', street: '' }
  const m = /\r?\n/.exec(s)
  if (!m || m.index === undefined) return { regionLine: s, street: '' }
  return {
    regionLine: s.slice(0, m.index).trim(),
    street: s.slice(m.index + m[0].length).trim(),
  }
}

function pickIsoDate(o: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = o[k]
    if (v == null || v === '') continue
    const s = String(v).trim()
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10)
  }
  return ''
}

/** 创建/更新/审核时间等 → 展示用字符串 */
function pickMetaDateTime(o: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = o[k]
    if (v == null || v === '') continue
    const s = String(v).trim()
    if (!s) continue
    if (s.includes('T')) {
      return s
        .replace('T', ' ')
        .replace(/\.\d+Z?$/, '')
        .slice(0, 19)
    }
    return s.length > 19 ? s.slice(0, 19) : s
  }
  return ''
}

/** 业务文件 ID / URL → 可给 a-image 使用的地址（与人员证件预览一致） */
export function companyFilePreviewUrl(fileId: string | undefined | null): string | null {
  const id = fileId?.trim()
  if (!id) return null
  if (/^https?:\/\//i.test(id)) return id
  if (id.startsWith('data:')) return id
  const base = (apiConfig.baseURL || '').replace(/\/$/, '')
  const path = `/dev/file/download?id=${encodeURIComponent(id)}`
  if (!base) return path
  return `${base}${path}`
}

/**
 * 将分页接口单条记录映射为列表行（含 listRawRecord 供查看详情展示完整接口字段）
 */
function mapOverseasEnterpriseRecordToCompanyItem(raw: unknown): CompanyListRow | null {
  if (!raw || typeof raw !== 'object') return null
  const o = raw as Record<string, unknown>
  const id = pickId(o)
  if (!id) return null

  const lon = pickNum(o, 'overseasLongitude', 'longitude')
  const lat = pickNum(o, 'overseasLatitude', 'latitude')
  let foreignGeoCoordinate = pickStr(o, 'foreignGeoCoordinate')
  if (!foreignGeoCoordinate && lon != null && lat != null) {
    foreignGeoCoordinate = `${lon},${lat}`
  }

  const daFull = pickStr(o, 'domesticAddress')
  const { regionLine, street } = splitDomesticStoredAddress(daFull)
  const domesticPath = regionLine || pickStr(o, 'domesticRegionPath')

  const industry =
    pickStr(o, 'industryCategory', 'industry_category') ||
    (() => {
      const scope = pickStr(o, 'businessScope')
      if (!scope) return '—'
      const line = scope.split(/[\r\n]/)[0]?.trim() ?? scope
      return line.length > 32 ? `${line.slice(0, 32)}…` : line
    })()

  return {
    id,
    unitType: mapUnitTypeFromRecord(o.type ?? o.enterpriseType ?? o.unitType ?? o.bizType),
    unitName: pickStr(o, 'name', 'unitName'),
    socialCreditCode: pickStr(o, 'creditCode', 'socialCreditCode'),
    industryCategory: industry,
    companyProperty: mapCompanyPropertyFromApi(o.enterpriseAttribute ?? o.companyProperty),
    domesticAddressPath: domesticPath || undefined,
    domesticStreetAddress: street || undefined,
    domesticGeonameid: pickNum(o, 'domesticGeonameid'),
    domesticAddressCode: pickStr(o, 'domesticAddressCode', 'domestic_address_code') || undefined,
    overseasGeonameid: pickNum(o, 'overseasGeonameid', 'overseas_geonameid'),
    overseasCountry: pickStr(o, 'overseasCountry', 'overseas_country') || undefined,
    overseasCountryName: pickStr(o, 'overseasCountryName', 'overseas_country_name') || undefined,
    overseasAddress: pickStr(o, 'overseasAddress'),
    overseasDetailAddress:
      pickStr(o, 'overseasDetailAddress', 'overseas_detail_address') || undefined,
    chineseEmployeeCount: pickNum(o, 'chineseEmployeeCount') ?? null,
    investmentField: pickStr(o, 'investmentField', 'investment_field') || undefined,
    establishMethod: pickStr(o, 'establishMethod', 'establish_method') || undefined,
    investmentScale: pickStr(o, 'investmentScale', 'investment_scale') || undefined,
    investmentRatio: pickStr(o, 'investmentRatio', 'investment_ratio') || undefined,
    operationStatus: pickStr(o, 'operationStatus', 'operation_status') || undefined,
    projectCategory: pickStr(o, 'projectCategory', 'project_category') || undefined,
    ownerEnterpriseName: pickStr(o, 'ownerEnterpriseName', 'owner_enterprise_name') || undefined,
    projectSummary: pickStr(o, 'projectSummary', 'project_summary') || undefined,
    contractAmountUsd: pickStr(o, 'contractAmountUsd', 'contract_amount_usd') || undefined,
    turnoverAmountUsd: pickStr(o, 'turnoverAmountUsd', 'turnover_amount_usd') || undefined,
    contractSignDate: pickIsoDate(o, 'contractSignDate', 'contract_sign_date') || undefined,
    domesticPrincipal: pickStr(o, 'domesticPrincipal', 'domestic_principal') || undefined,
    continent: pickStr(o, 'continent') || undefined,
    domesticDispatchAspect: pickStr(o, 'domesticDepartUnit', 'domesticDispatchAspect'),
    principal: pickStr(o, 'principalPerson', 'principal'),
    principalPhone: pickStr(o, 'contactPhone', 'principalPhone'),
    foreignGeoCoordinate,
    status: mapApiStatusToCompany(o.status),
    rejectReason: pickStr(o, 'auditRemark', 'rejectReason') || undefined,
    legalPerson: pickStr(o, 'legalPerson') || undefined,
    legalPersonPhone: pickStr(o, 'legalPersonPhone', 'legal_person_phone') || undefined,
    legalPersonIdCard: pickStr(o, 'legalPersonIdCard', 'legal_person_id_card') || undefined,
    registeredCapital: pickStr(o, 'registeredCapital') || undefined,
    establishDate: pickIsoDate(o, 'establishDate', 'establish_date') || undefined,
    businessScope: pickStr(o, 'businessScope') || undefined,
    businessTerm: pickStr(o, 'businessTerm') || undefined,
    issueLicenseDate: pickIsoDate(o, 'issueLicenseDate', 'issue_license_date') || undefined,
    businessLicensPhoto: pickStr(o, 'businessLicensPhoto', 'business_licens_photo') || undefined,
    createTime: pickMetaDateTime(o, 'createTime', 'create_time') || undefined,
    updateTime: pickMetaDateTime(o, 'updateTime', 'update_time') || undefined,
    createUser: pickStr(o, 'createUser', 'create_user') || undefined,
    createUserName: pickStr(o, 'createUserName', 'create_user_name') || undefined,
    createUserMode: pickNum(o, 'createUserMode', 'create_user_mode') ?? undefined,
    createUserAccount: pickStr(o, 'createUserAccount', 'create_user_account') || undefined,
    linkedUserId: pickStr(o, 'linkedUserId', 'linked_user_id') || undefined,
    linkedUserName: pickStr(o, 'linkedUserName', 'linked_user_name') || undefined,
    linkedUserMode: pickNum(o, 'linkedUserMode', 'linked_user_mode') ?? undefined,
    proxyForOthers: pickStr(o, 'proxyForOthers', 'proxy_for_others') || undefined,
    updateUser: pickStr(o, 'updateUser', 'update_user') || undefined,
    updateUserName: pickStr(o, 'updateUserName', 'update_user_name') || undefined,
    auditUser: pickStr(o, 'auditUser', 'audit_user') || undefined,
    auditUserName: pickStr(o, 'auditUserName', 'audit_user_name') || undefined,
    auditTime: pickMetaDateTime(o, 'auditTime', 'audit_time') || undefined,
    auditRemark: pickStr(o, 'auditRemark', 'audit_remark') || undefined,
    integrityStatus: pickStr(o, 'integrityStatus', 'integrity_status') || undefined,
    listRawRecord: { ...o },
  }
}

function buildCompanyPageQuery(
  params: CompanyPageParams,
): Record<string, string | number | undefined> {
  const q: Record<string, string | number | undefined> = {
    current: params.current,
    size: params.size,
    searchKey: params.searchKey?.trim() || undefined,
  }
  if (params.status && params.status !== 'all') {
    q.status = params.status
  }
  // 后端分页参数字段为 bizType（企业 / 项目）
  if (params.type && params.type !== 'all') {
    q.bizType = params.type === 'project' ? '项目' : '企业'
  }
  const ac = params.areaCode?.trim()
  if (ac) q.areaCode = ac
  return q
}

/**
 * GET /biz/overseas/enterprise/page
 * Query: current, size, searchKey, status, areaCode, type
 */
export async function fetchCompanyPage(params: CompanyPageParams): Promise<CompanyPageResult> {
  const raw = await http.get<OverseasEnterprisePageData>('/biz/overseas/enterprise/page', {
    params: buildCompanyPageQuery(params),
  })
  const records = Array.isArray(raw.records) ? raw.records : Array.isArray(raw.list) ? raw.list : []
  const list = records
    .map(mapOverseasEnterpriseRecordToCompanyItem)
    .filter((x): x is CompanyListRow => x != null)
  const parsed = Number(raw.total)
  const total = Number.isFinite(parsed) && parsed >= 0 ? Math.trunc(parsed) : list.length
  return { list, total }
}

/** 企业完整性状态 → 详情页标签文案与颜色 */
export function companyIntegrityStatusDisplay(status: string | undefined | null): {
  text: string
  color: 'success' | 'error' | 'default' | 'warning'
} {
  const u = String(status ?? '')
    .trim()
    .toUpperCase()
  if (u === 'VALID') return { text: '完整', color: 'success' }
  if (u === 'TAMPERED') return { text: '已篡改', color: 'error' }
  if (u === 'UNSIGNED') return { text: '未签名', color: 'warning' }
  if (u === 'DISABLED') return { text: '未启用', color: 'default' }
  return { text: '未知', color: 'default' }
}

/** 负责人手机号 / 信用代码：明文原样；密文或不规则长串固定 ****** */
export function maskPrincipalPhone(phone: string | undefined | null): string {
  return toPlainOrCipherPlaceholder(phone)
}

/** GET /biz/overseas/enterprise/detail?id= — 人员档案等场景按 id 展示企业名称 */
export async function fetchOverseasEnterpriseDetail(
  id: string,
): Promise<{ id: string; name: string; creditCode: string } | null> {
  const sid = id?.trim()
  if (!sid) return null
  try {
    const raw = await http.get<Record<string, unknown>>('/biz/overseas/enterprise/detail', {
      params: { id: sid },
      skipErrorMessage: true,
    })
    if (!raw || typeof raw !== 'object') return { id: sid, name: sid, creditCode: '' }
    const o = raw as Record<string, unknown>
    return {
      id: sid,
      name: pickStr(o, 'name') || sid,
      creditCode: pickStr(o, 'creditCode'),
    }
  } catch {
    return null
  }
}

export interface EnterpriseDropdownItem {
  id: string
  name: string
  creditCode: string
  principalPerson: string
  bizType: string
}

/** GET /biz/overseas/enterprise/dropdownList */
export async function fetchEnterpriseDropdownList(): Promise<EnterpriseDropdownItem[]> {
  const raw = await http.get<unknown>('/biz/overseas/enterprise/dropdownList')
  if (!Array.isArray(raw)) return []
  return raw
    .map((item) => {
      const o = item as Record<string, unknown>
      const id = o.id != null ? String(o.id).trim() : ''
      const name = o.name != null ? String(o.name).trim() : ''
      const creditCode = o.creditCode != null ? String(o.creditCode).trim() : ''
      const principalPerson = o.principalPerson != null ? String(o.principalPerson).trim() : ''
      const bizTypeRaw = o.bizType != null ? String(o.bizType).trim() : ''
      const unitType = mapUnitTypeFromRecord(bizTypeRaw)
      const bizType = unitType === 'project' ? '项目' : bizTypeRaw ? '企业' : ''
      return { id, name, creditCode, principalPerson, bizType }
    })
    .filter((x) => x.id && x.name)
}

/**
 * GET /biz/overseas/enterprise/detailPlain?id=
 * 负责人手机号明文（编辑回填；后端记审计日志）
 */
export async function fetchEnterpriseDetailPlain(id: string): Promise<CompanyItem | null> {
  const sid = id?.trim()
  if (!sid) return null
  const raw = await http.get<unknown>('/biz/overseas/enterprise/detailPlain', {
    params: { id: sid },
  })
  return enterpriseRecordToCompanyItem(raw)
}

/** GET /biz/overseas/enterprise/detail?id= — 含完整性状态；敏感字段为库列原值，不解密 */
export async function fetchCompanyDetail(id: string): Promise<CompanyItem | null> {
  const sid = id?.trim()
  if (!sid) return null
  const raw = await http.get<unknown>('/biz/overseas/enterprise/detail', {
    params: { id: sid },
  })
  return enterpriseRecordToCompanyItem(raw)
}

/**
 * 待后端联调：旧字段结构（若仍有调用可接真实接口）
 */
export async function updateCompanyDetail(id: string, payload: CompanySavePayload): Promise<void> {
  void id
  void payload
  // TODO: return http.put<void>(`/info/company/${id}`, payload)
}

function unwrapApiDataRecord(raw: unknown): Record<string, unknown> {
  if (raw == null || typeof raw !== 'object') return {}
  const o = raw as Record<string, unknown>
  const inner = o.data
  if (inner != null && typeof inner === 'object' && !Array.isArray(inner)) {
    return inner as Record<string, unknown>
  }
  return o
}

/**
 * 营业执照 OCR 日期：支持「2018年05月20日」、2018-5-20 等
 */
export function normalizeEnterpriseOcrDate(input: unknown): string {
  const s = String(input ?? '').trim()
  if (!s) return ''
  const cn = s.match(/(\d{4})\s*年\s*(\d{1,2})\s*月\s*(\d{1,2})\s*日?/)
  if (cn?.[1] && cn[2] && cn[3]) {
    return `${cn[1]}-${cn[2].padStart(2, '0')}-${cn[3].padStart(2, '0')}`
  }
  const iso = s.match(/(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/)
  if (iso?.[1] && iso[2] && iso[3]) {
    return `${iso[1]}-${iso[2].padStart(2, '0')}-${iso[3].padStart(2, '0')}`
  }
  return ''
}

/**
 * POST /biz/overseas/enterprise/ocr/businessLicense（multipart，字段名 businessLicense）
 * 返回英文字段见后端 BUSINESS_LICENSE_MAP；含 fileId（dev_file 主键）与 url（可访问地址，同 OverseasCommonService）
 */
export async function ocrEnterpriseBusinessLicense(file: File): Promise<Record<string, unknown>> {
  const fd = new FormData()
  fd.append('businessLicense', file)
  const raw = await http.upload<unknown>('/biz/overseas/enterprise/ocr/businessLicense', fd)
  return unwrapApiDataRecord(raw)
}

/** POST /biz/overseas/enterprise/add */
export async function addOverseasEnterprise(payload: EnterpriseAddPayload): Promise<void> {
  await http.post<void>('/biz/overseas/enterprise/add', payload)
}

/** POST /biz/overseas/enterprise/edit */
export async function editOverseasEnterprise(payload: EnterpriseEditPayload): Promise<void> {
  await http.post<void>('/biz/overseas/enterprise/edit', payload)
}

/** POST /biz/overseas/enterprise/delete */
export async function deleteOverseasEnterprise(id: string): Promise<void> {
  const sid = id?.trim()
  if (!sid) return
  await http.post<void>('/biz/overseas/enterprise/delete', { id: sid })
}

/** GET /biz/overseas/enterprise/auditHistory?enterpriseId= */
export async function fetchEnterpriseAuditHistory(
  enterpriseId: string,
): Promise<EnterpriseAuditHistoryItem[]> {
  const id = enterpriseId?.trim()
  if (!id) return []
  const raw = await http.get<unknown>('/biz/overseas/enterprise/auditHistory', {
    params: { enterpriseId: id },
  })
  if (Array.isArray(raw)) return raw as EnterpriseAuditHistoryItem[]
  return []
}

/** POST /biz/overseas/enterprise/import 返回 data */
export interface EnterpriseImportErrorItem {
  row: number
  message: string
}

export interface EnterpriseImportResult {
  totalCount: number
  successCount: number
  errorCount: number
  errorDetail: EnterpriseImportErrorItem[]
  failedExcelBase64?: string
  failedExcelFileName?: string
  failedExcelError?: string
}

function unwrapImportApiData(raw: unknown): Record<string, unknown> | null {
  if (raw == null || typeof raw !== 'object' || Array.isArray(raw)) return null
  const o = raw as Record<string, unknown>
  const inner = o.data
  if (inner != null && typeof inner === 'object' && !Array.isArray(inner)) {
    return inner as Record<string, unknown>
  }
  return o
}

function importResultNum(v: unknown): number {
  if (typeof v === 'number' && !Number.isNaN(v)) return v
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v)
    if (!Number.isNaN(n)) return n
  }
  return 0
}

function importResultPickStr(o: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = o[k]
    if (v != null && v !== '') return String(v)
  }
  return ''
}

function parseEnterpriseImportResult(raw: unknown): EnterpriseImportResult {
  const o = unwrapImportApiData(raw)
  if (!o) {
    return { totalCount: 0, successCount: 0, errorCount: 0, errorDetail: [] }
  }
  const errors: EnterpriseImportErrorItem[] = []
  const errRaw = o.errorDetail
  if (Array.isArray(errRaw)) {
    for (const item of errRaw) {
      if (item != null && typeof item === 'object' && !Array.isArray(item)) {
        const e = item as Record<string, unknown>
        errors.push({
          row: importResultNum(e.row),
          message: importResultPickStr(e, 'message', 'msg'),
        })
      }
    }
  }
  return {
    totalCount: importResultNum(o.totalCount),
    successCount: importResultNum(o.successCount),
    errorCount: importResultNum(o.errorCount),
    errorDetail: errors,
    failedExcelBase64: importResultPickStr(o, 'failedExcelBase64') || undefined,
    failedExcelFileName: importResultPickStr(o, 'failedExcelFileName') || undefined,
    failedExcelError: importResultPickStr(o, 'failedExcelError') || undefined,
  }
}

export async function downloadEnterpriseImportTemplate(
  kind: 'enterprise' | 'project' = 'enterprise',
): Promise<void> {
  const project = kind === 'project'
  const bizType = project ? 'PROJECT' : 'ENTERPRISE'
  const filename = project ? '黔籍在外项目批量导入模板.xlsx' : '黔籍在外企业批量导入模板.xlsx'
  await http.download(`/biz/overseas/enterprise/importTemplate?bizType=${bizType}`, filename)
}

export async function fetchEnterpriseImportTemplate(
  kind: 'enterprise' | 'project' = 'enterprise',
): Promise<ArrayBuffer> {
  const bizType = kind === 'project' ? 'PROJECT' : 'ENTERPRISE'
  const blob = await http.getBlob(`/biz/overseas/enterprise/importTemplate?bizType=${bizType}`)
  return blob.arrayBuffer()
}

function parseEnterpriseImportPreviewResult(
  raw: unknown,
): import('@/types/importPreview').ImportPreviewResult {
  const o = unwrapImportApiData(raw)
  if (!o) {
    return { totalCount: 0, importableCount: 0, errorCount: 0, rows: [] }
  }
  const rowsRaw = o.rows
  const rows: import('@/types/importPreview').ImportPreviewRow[] = []
  if (Array.isArray(rowsRaw)) {
    for (const item of rowsRaw) {
      if (!item || typeof item !== 'object') continue
      const r = item as Record<string, unknown>
      const dbFieldsRaw = r.dbFields
      const dbFields: import('@/types/importPreview').ImportDbFieldItem[] = []
      if (Array.isArray(dbFieldsRaw)) {
        for (const f of dbFieldsRaw) {
          if (!f || typeof f !== 'object') continue
          const x = f as Record<string, unknown>
          dbFields.push({
            column: importResultPickStr(x, 'column'),
            label: importResultPickStr(x, 'label'),
            value: importResultPickStr(x, 'value'),
            confidential: x.confidential === true,
            inferred: x.inferred === true,
            autoGenerated: x.autoGenerated === true,
          })
        }
      }
      rows.push({
        excelRow: importResultNum(r.excelRow),
        rowLabel: importResultPickStr(r, 'rowLabel') || '（无名称）',
        dbFields,
        warnings: Array.isArray(r.warnings) ? r.warnings.map(String) : [],
        errors: Array.isArray(r.errors) ? r.errors.map(String) : [],
        importable: r.importable === true || r.importable === 'true' || r.importable === 1,
      })
    }
  }
  return {
    totalCount: importResultNum(o.totalCount),
    importableCount: importResultNum(o.importableCount),
    errorCount: importResultNum(o.errorCount),
    icspEnabled: o.icspEnabled === true,
    confidentialHint: importResultPickStr(o, 'confidentialHint') || undefined,
    rows,
  }
}

/** POST /biz/overseas/enterprise/import/preview */
export async function previewOverseasEnterpriseImport(
  file: File,
): Promise<import('@/types/importPreview').ImportPreviewResult> {
  const fd = new FormData()
  fd.append('file', file)
  const raw = await http.upload<unknown>('/biz/overseas/enterprise/import/preview', fd, {
    timeout: OVERSEAS_IMPORT_HTTP_TIMEOUT_MS,
  })
  return parseEnterpriseImportPreviewResult(raw)
}

/** POST /biz/overseas/enterprise/import/preview/chunk */
export async function previewOverseasEnterpriseChunk(
  rows: import('@/types/importPreview').ImportChunkRowPayload[],
): Promise<import('@/types/importPreview').ImportPreviewResult> {
  const raw = await http.post<unknown>(
    '/biz/overseas/enterprise/import/preview/chunk',
    { rows },
    { timeout: OVERSEAS_IMPORT_PREVIEW_HTTP_TIMEOUT_MS, skipErrorMessage: true },
  )
  return parseImportPreviewResult(raw)
}

/** POST /biz/overseas/enterprise/import/prepare */
export async function prepareOverseasEnterpriseImport(): Promise<void> {
  await http.post<unknown>(
    '/biz/overseas/enterprise/import/prepare',
    {},
    { timeout: OVERSEAS_IMPORT_PREPARE_HTTP_TIMEOUT_MS, skipErrorMessage: true },
  )
}

/** POST /biz/overseas/enterprise/import/cache/refresh */
export async function refreshOverseasEnterpriseImportCache(): Promise<void> {
  await http.post<unknown>(
    '/biz/overseas/enterprise/import/cache/refresh',
    {},
    { timeout: OVERSEAS_IMPORT_PREPARE_HTTP_TIMEOUT_MS, skipErrorMessage: true },
  )
}

/** POST /biz/overseas/enterprise/import/chunk */
export async function importOverseasEnterpriseChunk(
  rows: import('@/types/importPreview').ImportChunkRowPayload[],
): Promise<import('@/types/importPreview').ImportChunkResult> {
  const raw = await http.post<unknown>(
    '/biz/overseas/enterprise/import/chunk',
    { rows },
    { timeout: OVERSEAS_IMPORT_HTTP_TIMEOUT_MS, skipErrorMessage: true },
  )
  return parseImportChunkResult(raw)
}

/** POST /biz/overseas/enterprise/import（multipart，字段名 file） */
export async function importOverseasEnterpriseFile(file: File): Promise<EnterpriseImportResult> {
  const fd = new FormData()
  fd.append('file', file)
  const raw = await http.upload<unknown>('/biz/overseas/enterprise/import', fd)
  return parseEnterpriseImportResult(raw)
}

/**
 * GET /biz/overseas/enterprise/export — 导出 Excel（筛选条件与列表一致）
 * @param options.decryptSensitive 为 true 时解密信用代码、负责人手机号；默认 false 加密导出
 */
export async function exportOverseasEnterpriseData(
  params: CompanyPageParams,
  options?: { decryptSensitive?: boolean; decryptBatchSize?: number },
): Promise<void> {
  const query = buildEnterpriseExportQuery(params, options)
  await http.download('/biz/overseas/enterprise/export', '黔籍在外企业导出.xlsx', {
    params: query,
    timeout: 120000,
  })
}

function buildEnterpriseExportQuery(
  params: CompanyPageParams,
  options?: { decryptSensitive?: boolean; decryptBatchSize?: number },
): Record<string, string | number | boolean | undefined> {
  const query: Record<string, string | number | boolean | undefined> = buildCompanyPageQuery(params)
  delete query.current
  delete query.size
  query.decryptSensitive = options?.decryptSensitive === true ? true : undefined
  query.decryptBatchSize =
    options?.decryptSensitive === true && options.decryptBatchSize != null
      ? options.decryptBatchSize
      : undefined
  return query
}

export async function startEnterpriseExportJob(
  params: CompanyPageParams,
  options: { decryptSensitive: boolean; decryptBatchSize: number },
): Promise<OverseasExportJobStart> {
  const raw = await http.post<unknown>(
    '/biz/overseas/enterprise/export/job',
    {},
    {
      params: buildEnterpriseExportQuery(params, options),
      timeout: 120000,
      skipErrorMessage: true,
    },
  )
  const started = asJobStart(raw)
  if (!started.jobId) {
    throw new Error('未能创建导出任务')
  }
  return started
}

export async function fetchEnterpriseExportJobStatus(
  jobId: string,
): Promise<OverseasExportJobStatus> {
  const raw = await http.get<unknown>(
    `/biz/overseas/enterprise/export/job/${encodeURIComponent(jobId)}`,
    {
      skipErrorMessage: true,
      timeout: 20000,
    },
  )
  return asJobStatus(raw)
}

export async function downloadEnterpriseExportJobFile(jobId: string): Promise<void> {
  await downloadExportJobExcel(
    `/biz/overseas/enterprise/export/job/${encodeURIComponent(jobId)}/file`,
    '黔籍在外企业导出.xlsx',
  )
}

/** POST /biz/overseas/enterprise/audit */
export interface EnterpriseAuditPayload {
  id: string
  status: 'APPROVED' | 'REJECTED'
  /** 驳回原因；已通过时传空字符串 */
  auditRemark: string
}

/**
 * 待后端联调：旧字段结构（批量导入等）
 */
export async function createCompany(payload: CompanySavePayload): Promise<void> {
  void payload
  // TODO: return http.post<void>('/info/company', payload)
}

/**
 * 待后端联调：POST /info/company/batch-import
 */
export async function createCompanyBatch(payload: CompanySavePayload[]): Promise<void> {
  void payload
  // TODO: return http.post<void>('/info/company/batch-import', payload)
}

/**
 * 企业审核（列表操作）
 * POST /biz/overseas/enterprise/audit
 */
export async function auditCompany(
  id: string,
  payload: { status: Extract<CompanyStatus, 'normal' | 'rejected'>; rejectReason?: string },
): Promise<void> {
  const apiStatus: EnterpriseAuditPayload['status'] =
    payload.status === 'normal' ? 'APPROVED' : 'REJECTED'
  const body: EnterpriseAuditPayload = {
    id,
    status: apiStatus,
    auditRemark: apiStatus === 'REJECTED' ? (payload.rejectReason?.trim() ?? '') : '',
  }
  await http.post<void>('/biz/overseas/enterprise/audit', body)
}
